import React, { useState, useMemo, useCallback } from "react";
import Markdown from "react-markdown";
import type {
  BlockDocumentContent,
  Block,
  SectionBlock,
  TextBlock,
  TextSegment,
  TableBlock,
  TocBlock,
} from "@unruly-global/api-types";
import { DyadMatrixRenderer } from "./DyadMatrixRenderer";
import { DYAD_STATUS_PALETTE, extractCountries } from "./dyadConstants";
import {
  stripObjectReferences,
  sanitizeCaption,
} from "../../helper/blockDocument";
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from "@mui/icons-material";
import SourcePill from "../SourcePill/SourcePill";
import { EventSourcesMap } from "../../common-types/EventSource";
import type { Osdk } from "@osdk/client";
import type { Analysis } from "@unruly-osdk/sdk";
import { findSubEntryUid } from "./tocUtils";
import { AssessmentDetailView } from "./AssessmentDetailView";
import styles from "./BlockDocumentRenderer.module.css";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface BlockDocumentRendererProps {
  document: BlockDocumentContent;
  reportType?: string;
  eventSources: EventSourcesMap;
  isEventSourcesLoading: boolean;
  missingEventIds: string[];
  userId?: string;
  sourceNumberMap: Map<string, number>;
  onOpenAnalysis?: (analysis: Osdk.Instance<Analysis>, eventId: string) => void;
  showShareDialog: (eventId: string) => void;
}

// ---------------------------------------------------------------------------
// Source pills for a block
// ---------------------------------------------------------------------------

interface BlockSourcesProps {
  sources?: string[];
  eventSources: EventSourcesMap;
  isEventSourcesLoading: boolean;
  missingEventIds: string[];
  userId?: string;
  sourceNumberMap: Map<string, number>;
  onOpenAnalysis?: (analysis: Osdk.Instance<Analysis>, eventId: string) => void;
  showShareDialog: (eventId: string) => void;
}

const BlockSources: React.FC<BlockSourcesProps> = ({
  sources,
  eventSources,
  isEventSourcesLoading: isLoading,
  missingEventIds,
  userId,
  sourceNumberMap,
  onOpenAnalysis,
  showShareDialog,
}) => {
  if (!sources || sources.length === 0) return null;

  return (
    <span className={styles.sources}>
      {sources.map((eventId) => {
        const eventData = eventSources[eventId];
        const isMissing = missingEventIds.includes(eventId);

        if (isLoading) {
          return (
            <span key={eventId} className={styles.sourceLoading}>
              ...
            </span>
          );
        }

        if (isMissing) {
          return (
            <span
              key={eventId}
              className={styles.sourceMissing}
              title={eventId}
            >
              {eventId.slice(0, 4)}...{eventId.slice(-4)}
            </span>
          );
        }

        if (!eventData) return null;

        return (
          <React.Fragment key={eventId}>
            <SourcePill
              sourceUrl={eventData.sourceUrl}
              sourceLabel={eventData.sourceLabel}
              sourceName={eventData.sourceName}
              eventId={eventId}
              eventName={eventData.eventName}
              eventSummary={eventData.eventSummary}
              eventDate={eventData.eventDate}
              userId={userId}
              sourceNumber={sourceNumberMap.get(eventId)}
              onOpenAnalysis={onOpenAnalysis}
              showShareDialog={showShareDialog}
            />{" "}
          </React.Fragment>
        );
      })}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Dyad status badge rendering
// ---------------------------------------------------------------------------

const TRAJECTORY_ICONS: Record<string, React.ReactNode> = {
  Improving: <TrendingUpIcon className={styles.trajectoryIcon} />,
  Worsening: <TrendingDownIcon className={styles.trajectoryIcon} />,
};

/** Neutral trajectory carries no signal — treat as absent. */
function meaningfulTrajectory(t: string | undefined): string | undefined {
  if (!t || t.toLowerCase() === "neutral") return undefined;
  return t;
}

const DyadBadges: React.FC<{
  statusScore?: number;
  trajectory?: string;
}> = ({ statusScore, trajectory }) => {
  if (!statusScore) return null;
  const palette = DYAD_STATUS_PALETTE[statusScore];
  if (!palette) return null;

  return (
    <span className={styles.captionBadges}>
      <span
        className={styles.badge}
        style={{ backgroundColor: palette.bg, color: palette.text }}
      >
        {palette.label}
      </span>
      {trajectory && (
        <span className={styles.trajectoryBadge}>
          {TRAJECTORY_ICONS[trajectory]}
          {trajectory}
        </span>
      )}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Title + caption row
// ---------------------------------------------------------------------------

const TitleRow: React.FC<{
  title?: string;
  caption?: string;
  headline?: string;
  statusScore?: number;
  trajectory?: string;
  reportType?: string;
}> = ({ title, caption, headline, statusScore, trajectory, reportType }) => {
  const isDyad = reportType === "dyad" || !!statusScore;

  if (isDyad) {
    const cleanCaption = sanitizeCaption(caption);
    const resolvedScore = statusScore;
    const resolvedTrajectory = meaningfulTrajectory(trajectory);

    if (!title && !cleanCaption && !resolvedScore) return null;

    return (
      <div className={styles.titleRowDyad}>
        <div className={styles.dyadTitleLine}>
          {title && <h3 className={styles.dyadBlockTitle}>{title}</h3>}
          {resolvedScore ? (
            <DyadBadges
              statusScore={resolvedScore}
              trajectory={resolvedTrajectory}
            />
          ) : (
            cleanCaption && (
              <span className={styles.caption}>{cleanCaption}</span>
            )
          )}
        </div>
        {headline && <p className={styles.dyadHeadline}>{headline}</p>}
      </div>
    );
  }

  // Non-dyad: original rendering
  if (!title && !caption) return null;
  return (
    <div className={styles.titleRow}>
      {title && <h3 className={styles.blockTitle}>{title}</h3>}
      {caption && <span className={styles.caption}>{caption}</span>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Numeric column detection
// ---------------------------------------------------------------------------

function isNumericColumn(rows: string[][], colIndex: number): boolean {
  if (rows.length === 0) return false;
  let hasNonEmpty = false;
  for (const row of rows) {
    const val = row[colIndex]?.trim();
    if (!val) continue;
    hasNonEmpty = true;
    if (!/^[+\-$€£]?[\d,.]*\d[\d,.]*%?$/.test(val)) return false;
  }
  return hasNonEmpty;
}

// ---------------------------------------------------------------------------
// Individual block renderers
// ---------------------------------------------------------------------------

interface SourceProps {
  eventSources: EventSourcesMap;
  isEventSourcesLoading: boolean;
  missingEventIds: string[];
  userId?: string;
  sourceNumberMap: Map<string, number>;
  onOpenAnalysis?: (analysis: Osdk.Instance<Analysis>, eventId: string) => void;
  showShareDialog: (eventId: string) => void;
}

const SectionBlockRenderer: React.FC<{ block: SectionBlock }> = ({ block }) => (
  <div id={`block-${block.uid}`} className={styles.section}>
    {block.title && <h2 className={styles.sectionTitle}>{block.title}</h2>}
    {block.caption && (
      <span className={styles.sectionCaption}>{block.caption}</span>
    )}
  </div>
);

const SegmentRenderer: React.FC<{ segment: TextSegment } & SourceProps> = ({
  segment,
  ...sourceProps
}) => (
  <span className={styles.segment}>
    {stripObjectReferences(segment.text)}
    <BlockSources sources={segment.sources} {...sourceProps} />{" "}
  </span>
);

const AnalysisContent: React.FC<{ block: TextBlock } & SourceProps> = ({
  block,
  ...sourceProps
}) => {
  const hasSegments = block.segments && block.segments.length > 0;
  return (
    <div className={`${styles.textContent} ${styles.textContentDyad}`}>
      {hasSegments ? (
        block.segments!.map((seg, i) => (
          <SegmentRenderer key={i} segment={seg} {...sourceProps} />
        ))
      ) : (
        <>
          <Markdown>{stripObjectReferences(block.text ?? "")}</Markdown>
          <BlockSources sources={block.sources} {...sourceProps} />
        </>
      )}
    </div>
  );
};

const TextBlockRenderer: React.FC<
  { block: TextBlock; reportType?: string } & SourceProps
> = ({ block, reportType, ...sourceProps }) => {
  const isDyad = reportType === "dyad" || !!block.statusScore || !!block.dyadId;
  const hasSegments = isDyad && block.segments && block.segments.length > 0;
  const isCollapsible =
    isDyad && (hasSegments || block.text) && block.shortSummary;
  const [expanded, setExpanded] = useState(false);
  const hasDyadDetail = isDyad && !!(block.dyadId || block.assessmentId);

  return (
    <div
      id={`block-${block.uid}`}
      className={`${isDyad ? styles.textBlockDyad : styles.textBlock}${isDyad && expanded ? ` ${styles.textBlockDyadExpanded}` : ""}`}
      data-dyad-id={block.dyadId}
      data-assessment-id={block.assessmentId}
    >
      <TitleRow
        title={block.title}
        caption={block.caption}
        headline={block.headline}
        statusScore={block.statusScore}
        trajectory={block.trajectory}
        reportType={reportType}
      />

      {isCollapsible ? (
        <>
          {/* Short summary — always visible, de-emphasized when expanded */}
          <p
            className={`${styles.dyadShortSummary} ${expanded ? styles.dyadShortSummaryDimmed : ""}`}
          >
            {block.shortSummary}
          </p>

          {/* Single unified toggle */}
          <button
            className={`${styles.dyadExpandBtn} ${expanded ? styles.dyadExpandBtnActive : ""}`}
            onClick={() => setExpanded((prev) => !prev)}
          >
            <svg
              className={styles.dyadExpandBtnIcon}
              viewBox="0 0 16 16"
              fill="currentColor"
            >
              {expanded ? (
                <path d="M4.646 9.646a.5.5 0 0 1 0-.708l3-3a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8 7.207 5.354 9.854a.5.5 0 0 1-.708-.208z" />
              ) : (
                <path d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z" />
              )}
            </svg>
            {expanded ? "Hide Analysis" : "Show Analysis"}
          </button>

          {/* Unified expanded section: detail card wrapping analysis */}
          {hasDyadDetail ? (
            <AssessmentDetailView
              dyadId={block.dyadId}
              assessmentId={block.assessmentId}
              statusScore={block.statusScore}
              trajectory={block.trajectory}
              open={expanded}
            >
              <AnalysisContent block={block} {...sourceProps} />
            </AssessmentDetailView>
          ) : (
            <div
              className={`${styles.dyadDetailWrapper} ${expanded ? styles.dyadDetailOpen : ""}`}
            >
              <div className={styles.dyadDetailInner}>
                <AnalysisContent block={block} {...sourceProps} />
              </div>
            </div>
          )}
        </>
      ) : isDyad ? (
        <>
          {/* Non-collapsible dyad: still offer the expand for detail + text */}
          {hasDyadDetail && (
            <>
              <button
                className={`${styles.dyadExpandBtn} ${expanded ? styles.dyadExpandBtnActive : ""}`}
                onClick={() => setExpanded((prev) => !prev)}
              >
                <svg
                  className={styles.dyadExpandBtnIcon}
                  viewBox="0 0 16 16"
                  fill="currentColor"
                >
                  {expanded ? (
                    <path d="M4.646 9.646a.5.5 0 0 1 0-.708l3-3a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8 7.207 5.354 9.854a.5.5 0 0 1-.708-.208z" />
                  ) : (
                    <path d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z" />
                  )}
                </svg>
                {expanded ? "Hide Details" : "View Details"}
              </button>
              <AssessmentDetailView
                dyadId={block.dyadId}
                assessmentId={block.assessmentId}
                statusScore={block.statusScore}
                trajectory={block.trajectory}
                open={expanded}
              />
            </>
          )}
          <div className={`${styles.textContent} ${styles.textContentDyad}`}>
            {hasSegments ? (
              block.segments!.map((seg, i) => (
                <SegmentRenderer key={i} segment={seg} {...sourceProps} />
              ))
            ) : (
              <>
                <Markdown>{stripObjectReferences(block.text ?? "")}</Markdown>
                <BlockSources sources={block.sources} {...sourceProps} />
              </>
            )}
          </div>
        </>
      ) : (
        <div className={styles.textContent}>
          <Markdown>{block.text}</Markdown>
          <BlockSources sources={block.sources} {...sourceProps} />
        </div>
      )}
    </div>
  );
};

const TableBlockRenderer: React.FC<{ block: TableBlock } & SourceProps> = ({
  block,
  ...sourceProps
}) => {
  const numericCols = block.data.columns.map((_, i) =>
    isNumericColumn(block.data.rows, i),
  );

  return (
    <div id={`block-${block.uid}`} className={styles.tableSection}>
      <TitleRow title={block.title} caption={block.caption} />
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead className={styles.thead}>
            <tr>
              {block.data.columns.map((col, i) => (
                <th key={i} className={styles.th}>
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.data.rows.map((row, rowIdx) => (
              <tr key={rowIdx} className={styles.tr}>
                {block.data.columns.map((_, cellIdx) => (
                  <td
                    key={cellIdx}
                    className={`${styles.td}${numericCols[cellIdx] ? ` ${styles.tdNumeric}` : ""}`}
                  >
                    {row[cellIdx] ?? ""}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {block.sources && block.sources.length > 0 && (
        <div className={styles.tableSources}>
          <BlockSources sources={block.sources} {...sourceProps} />
        </div>
      )}
    </div>
  );
};

// TOC block renderer
// ---------------------------------------------------------------------------

const SCROLL_OFFSET = 80;

function scrollToBlockUid(uid: string) {
  const el = document.getElementById(`block-${uid}`);
  if (!el) return;
  const y = el.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET;
  window.scrollTo({ top: y, behavior: "smooth" });
}

export const TocBlockRenderer: React.FC<{
  block: TocBlock;
  allBlocks: Block[];
}> = ({ block, allBlocks }) => {
  const [collapsed, setCollapsed] = useState(true);
  const [pinned, setPinned] = useState(false);

  if (block.entries.length === 0) return null;

  return (
    <div
      id={`block-${block.uid}`}
      data-no-copy
      className={`${styles.toc} ${pinned ? `${styles.tocPinned} ${styles.tocScrolled}` : ""}`}
    >
      <div className={styles.tocHeader}>
        <button
          type="button"
          className={styles.tocToggle}
          onClick={() => setCollapsed((c) => !c)}
        >
          <span className={styles.tocTitle}>Table of Contents</span>
        </button>
        {!collapsed && (
          <button
            type="button"
            className={`${styles.tocPinBtn} ${pinned ? styles.tocPinBtnActive : ""}`}
            onClick={() => setPinned((p) => !p)}
            title={pinned ? "Unpin" : "Pin to screen"}
          >
            <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
              <path d="M9.828.722a.5.5 0 0 1 .354.146l4.95 4.95a.5.5 0 0 1-.707.707l-.707-.707-3.182 3.182a4 4 0 0 1-1.328.886l-.207.088-1.086 4.346a.5.5 0 0 1-.84.243L4.5 11.988l-3.146 3.147a.5.5 0 0 1-.708-.708L3.793 11.28.718 8.207a.5.5 0 0 1 .243-.84L5.307 6.28a4 4 0 0 1 .886-1.328l3.182-3.182-.707-.707a.5.5 0 0 1 .146-.854z" />
            </svg>
          </button>
        )}
      </div>
      <ul
        className={`${styles.tocList} ${collapsed ? styles.tocListCollapsed : ""}`}
      >
        {block.entries.map((entry, i) => {
          const hasTarget = allBlocks.some((b) => b.uid === entry.targetUid);

          return (
            <li key={i} className={styles.tocEntry}>
              <div className={styles.tocHeadingRow}>
                {hasTarget ? (
                  <button
                    type="button"
                    className={styles.tocHeading}
                    onClick={() => scrollToBlockUid(entry.targetUid)}
                  >
                    {entry.heading}
                  </button>
                ) : (
                  <span className={styles.tocHeadingInert}>
                    {entry.heading}
                  </span>
                )}
              </div>
              {entry.subEntries.length > 0 && (
                <ul className={styles.tocSubList}>
                  {entry.subEntries
                    .filter((sub) => sub !== entry.heading)
                    .map((sub, j) => {
                      const subUid = findSubEntryUid(
                        sub,
                        entry.targetUid,
                        allBlocks,
                      );
                      return (
                        <li key={j} className={styles.tocSubEntry}>
                          {subUid ? (
                            <button
                              type="button"
                              className={styles.tocSubLink}
                              onClick={() => scrollToBlockUid(subUid)}
                            >
                              {sub}
                            </button>
                          ) : (
                            <span className={styles.tocSubInert}>{sub}</span>
                          )}
                        </li>
                      );
                    })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Status filter pills (dyad reports)
// ---------------------------------------------------------------------------

const STATUS_FILTER_OPTIONS = Object.entries(DYAD_STATUS_PALETTE)
  .sort(([a], [b]) => Number(b) - Number(a))
  .map(([, v]) => v);

const StatusFilters: React.FC<{
  activeFilters: Set<string>;
  onToggle: (label: string) => void;
}> = ({ activeFilters, onToggle }) => (
  <nav className={styles.filterBar}>
    <span className={styles.filterLabel}>Status</span>
    {STATUS_FILTER_OPTIONS.map(({ label, bg, text: textColor }) => {
      const active = activeFilters.has(label);
      return (
        <button
          key={label}
          className={`${styles.filterPill}${active ? ` ${styles.filterPillActive}` : ""}`}
          style={
            active
              ? { backgroundColor: bg, borderColor: bg, color: textColor }
              : { borderColor: bg, color: bg }
          }
          onClick={() => onToggle(label)}
        >
          {label}
        </button>
      );
    })}
    {activeFilters.size > 0 && (
      <button
        className={styles.filterClear}
        onClick={() => {
          for (const { label } of STATUS_FILTER_OPTIONS) {
            if (activeFilters.has(label)) onToggle(label);
          }
        }}
      >
        Clear
      </button>
    )}
  </nav>
);

// ---------------------------------------------------------------------------
// Country filter pills (dyad reports)
// ---------------------------------------------------------------------------

const CountryFilters: React.FC<{
  countries: string[];
  activeCountries: Set<string>;
  onToggle: (country: string) => void;
}> = ({ countries, activeCountries, onToggle }) => {
  if (countries.length === 0) return null;

  return (
    <nav className={styles.countryFilterBar}>
      <span className={styles.filterLabel}>Country</span>
      {countries.map((c) => {
        const active = activeCountries.has(c);
        return (
          <button
            key={c}
            className={`${styles.filterPill}${active ? ` ${styles.filterPillActive}` : ""}`}
            style={
              active
                ? {
                    backgroundColor: "var(--main-text, #374151)",
                    borderColor: "var(--main-text, #374151)",
                    color: "#fff",
                  }
                : undefined
            }
            onClick={() => onToggle(c)}
          >
            {c}
          </button>
        );
      })}
      {activeCountries.size > 0 && (
        <button
          className={styles.filterClear}
          onClick={() => {
            for (const c of countries) {
              if (activeCountries.has(c)) onToggle(c);
            }
          }}
        >
          Clear
        </button>
      )}
    </nav>
  );
};

// ---------------------------------------------------------------------------
// Build dyad → block uid map for click navigation
// ---------------------------------------------------------------------------

function buildDyadBlockMap(blocks: Block[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const block of blocks) {
    if (block.type === "text" && block.title) {
      if (!map.has(block.title)) {
        map.set(block.title, block.uid);
      }
    }
  }
  return map;
}

// ---------------------------------------------------------------------------
// Main renderer
// ---------------------------------------------------------------------------

function renderBlock(
  block: Block,
  sourceProps: SourceProps,
  allBlocks: Block[],
  reportType?: string,
  options?: {
    skipTables: boolean;
    activeFilters: Set<string>;
    countryFilters: Set<string>;
    dyadBlockMap: Map<string, string>;
  },
) {
  switch (block.type) {
    case "section":
      if (reportType === "dyad") return null;
      return <SectionBlockRenderer key={block.uid} block={block} />;
    case "text":
      return (
        <TextBlockRenderer
          key={block.uid}
          block={block}
          reportType={reportType}
          {...sourceProps}
        />
      );
    case "table":
      if (options?.skipTables) return null;
      return (
        <TableBlockRenderer key={block.uid} block={block} {...sourceProps} />
      );
    case "toc":
      return (
        <TocBlockRenderer key={block.uid} block={block} allBlocks={allBlocks} />
      );
    case "dyad_matrix":
      return (
        <DyadMatrixRenderer
          key={block.uid}
          block={block}
          activeFilters={
            options && options.activeFilters.size > 0
              ? options.activeFilters
              : undefined
          }
          countryFilters={
            options && options.countryFilters.size > 0
              ? options.countryFilters
              : undefined
          }
          dyadBlockMap={options?.dyadBlockMap}
        />
      );
    default: {
      const _exhaustive: never = block;
      void _exhaustive;
      return null;
    }
  }
}

export const BlockDocumentRenderer: React.FC<BlockDocumentRendererProps> = ({
  document: doc,
  reportType,
  eventSources,
  isEventSourcesLoading,
  missingEventIds,
  userId,
  sourceNumberMap,
  onOpenAnalysis,
  showShareDialog,
}) => {
  const sourceProps: SourceProps = {
    eventSources,
    isEventSourcesLoading,
    missingEventIds,
    userId,
    sourceNumberMap,
    onOpenAnalysis,
    showShareDialog,
  };

  const isDyad = reportType === "dyad";
  const hasMatrix = useMemo(
    () => doc.blocks.some((b) => b.type === "dyad_matrix"),
    [doc.blocks],
  );

  // Dyad filter state
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set());
  const toggleFilter = useCallback((label: string) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }, []);

  const [countryFilters, setCountryFilters] = useState<Set<string>>(new Set());
  const toggleCountry = useCallback((country: string) => {
    setCountryFilters((prev) => {
      const next = new Set(prev);
      if (next.has(country)) next.delete(country);
      else next.add(country);
      return next;
    });
  }, []);

  const allCountries = useMemo(() => {
    if (!isDyad) return [];
    const matrixBlock = doc.blocks.find((b) => b.type === "dyad_matrix");
    if (!matrixBlock || matrixBlock.type !== "dyad_matrix") return [];
    return extractCountries(matrixBlock.dyads);
  }, [isDyad, doc.blocks]);

  const dyadBlockMap = useMemo(
    () => (isDyad ? buildDyadBlockMap(doc.blocks) : new Map()),
    [isDyad, doc.blocks],
  );

  const renderOptions = useMemo(
    () => ({
      skipTables: isDyad && hasMatrix,
      activeFilters,
      countryFilters,
      dyadBlockMap,
    }),
    [isDyad, hasMatrix, activeFilters, countryFilters, dyadBlockMap],
  );

  // Filter text blocks when dyad status or country filters are active
  const visibleBlocks = useMemo(() => {
    const shouldFilter =
      isDyad &&
      hasMatrix &&
      (activeFilters.size > 0 || countryFilters.size > 0);
    if (!shouldFilter) return doc.blocks;

    const matchesFilter = (block: Block): boolean => {
      if (block.type !== "text") return false;
      if (activeFilters.size > 0) {
        const label = block.statusScore
          ? DYAD_STATUS_PALETTE[block.statusScore]?.label
          : undefined;
        if (label && !activeFilters.has(label)) return false;
      }
      if (countryFilters.size > 0 && block.title) {
        const hasMatch = Array.from(countryFilters).some((c) =>
          block.title!.includes(c),
        );
        if (!hasMatch) return false;
      }
      return true;
    };

    return doc.blocks.filter((block) => {
      if (block.type === "dyad_matrix") return true;
      if (block.type === "text") return matchesFilter(block);
      return true;
    });
  }, [isDyad, hasMatrix, doc.blocks, activeFilters, countryFilters]);

  return (
    <div className={styles.blockDocument}>
      {isDyad && hasMatrix && (
        <>
          <StatusFilters
            activeFilters={activeFilters}
            onToggle={toggleFilter}
          />
          <CountryFilters
            countries={allCountries}
            activeCountries={countryFilters}
            onToggle={toggleCountry}
          />
        </>
      )}
      {visibleBlocks.map((block) =>
        renderBlock(block, sourceProps, doc.blocks, reportType, renderOptions),
      )}
    </div>
  );
};

export default BlockDocumentRenderer;
